/**
 * Экземпляры платежей — единственный источник обязательных сумм.
 *
 * Раньше каждый движок сам складывал обязательства и считал, что их ровно по
 * одному в месяц. За месяц может наступить четыре недельных платежа и ни
 * одного годового, поэтому сумма «всех активных обязательств» не отвечает ни
 * на один вопрос о деньгах.
 *
 * Здесь календарь строится один раз, и им пользуются «Можно потратить»,
 * план периода, прогноз покупки и приоритеты.
 */
import { ZERO, add, clampPos, sub, type Money } from './money.js';
import { addDays, daysInMonth, isAfter, parts, ymd, type PlainDate } from './date.js';
import {
  anchorForDayOfMonth, monthlyOn, occurrencesBetween, type PaymentSchedule,
} from './schedule.js';
import type { Account, Obligation, ObligationPayment, Reserve } from './types.js';

/**
 * Расписание обязательства.
 *
 * Старые записи знают только число месяца — для них строится месячное
 * повторение с опорной датой, сохраняющей **исходный день**: платёж 31-го
 * числа не должен после февраля навсегда переехать на 28-е.
 */
export function obligationSchedule(o: Obligation, today: PlainDate): PaymentSchedule {
  return o.schedule ?? monthlyOn(anchorForDayOfMonth(o.dayOfMonth, today), o.shiftRule);
}

/** Сумма одной оплаты: у переменных берём осторожную оценку. */
export const instanceAmount = (o: Obligation): Money =>
  o.amountType === 'fixed' ? o.amount : (o.amountEstimate ?? o.amount);

export interface PaymentInstance {
  /** Устойчивый ключ наступления: обязательство плюс дата по расписанию. */
  id: string;
  obligationId: string;
  label: string;
  /** Дата по расписанию — часть идентичности, не меняется никогда. */
  scheduledDate: PlainDate;
  amount: Money;
  /** Сколько закреплено конвертами. */
  secured: Money;
  /** Сколько ещё не обеспечено. */
  remaining: Money;
  /** Сколько по этому наступлению уже оплачено. */
  paid: Money;
  /** Сколько ещё предстоит заплатить: требование минус оплаченное. */
  unpaid: Money;
  /**
   * Платёж выполнен полностью — подтверждение человека, а не наличие денег.
   *
   * Частичная оплата сюда не попадает: платёж, внесённый наполовину, всё ещё
   * ждёт денег, и назвать его выполненным значило бы обещать человеку то,
   * чего нет.
   */
  settled: boolean;
  /** Срок наступил, платёж не выполнен. Обеспеченность на это не влияет. */
  due: boolean;
  accountId: string;
}

export interface InstancesInput {
  obligations: readonly Obligation[];
  reserves?: readonly Reserve[];
  today: PlainDate;
  from: PlainDate;
  to: PlainDate;
}

/**
 * Все наступления платежей в отрезке, по возрастанию даты.
 *
 * Конверт под обязательство один, а наступлений в отрезке может быть
 * несколько — тогда он закрывает ближайшее: деньги нельзя закрепить дважды.
 */
export function paymentInstances(input: InstancesInput): PaymentInstance[] {
  const { today, from, to } = input;
  const reserves = input.reserves ?? [];
  const out: PaymentInstance[] = [];

  for (const o of input.obligations) {
    if (!o.isActive) continue;
    const dates = occurrencesBetween(obligationSchedule(o, today), from, to);
    if (dates.length === 0) continue;

    // Конверты под это обязательство — общий запас на все его наступления.
    let envelope = add(
      ...reserves.filter((r) => r.forKind === 'obligation' && r.forId === o.id).map((r) => r.amount),
    );

    for (const scheduledDate of dates) {
      const amount = requiredFor(o, factFor(o, scheduledDate));
      const paid = paidFor(o, scheduledDate, amount);
      const unpaid = clampPos(sub(amount, paid));
      const settled = unpaid === ZERO;
      // Конверт закрывает только неоплаченный остаток: закреплять деньги под
      // то, что уже ушло со счёта, значит держать их взаперти без причины.
      const secured = minMoney(envelope, unpaid);
      envelope = sub(envelope, secured);

      out.push({
        id: `${o.id}:${scheduledDate}`,
        obligationId: o.id,
        label: o.name,
        scheduledDate,
        amount,
        paid,
        unpaid,
        secured,
        remaining: clampPos(sub(unpaid, secured)),
        settled,
        due: !settled && !isAfter(scheduledDate, today),
        accountId: o.accountId,
      });
    }
  }

  return out.sort((a, b) => (a.scheduledDate < b.scheduledDate ? -1 : a.scheduledDate > b.scheduledDate ? 1 : 0));
}

const minMoney = (a: Money, b: Money): Money => (a <= b ? a : b);

/**
 * Конверты — насколько их правда держат деньги на своём счёте.
 *
 * Конверт — запись «эти деньги лежат под платёж». На счёте «не для трат» она
 * ничем не проверялась: у владельца под кредит авто (15 000) и рассрочку
 * (6 131) было отложено 21 131 ₽ на счёте, где лежало 16 152. Главная писала
 * «отложено» и «платежи обеспечены», а после оплаты авто на рассрочку
 * оставалось 1 152 ₽ из 6 131.
 *
 * Поэтому на таких счетах конверты урезаются до остатка — и в порядке сроков:
 * раньше наступающий платёж обеспечивается первым, как и будет оплачен.
 * Цели и прочие назначения — после платежей: их срок не завтра.
 *
 * Счета для трат не трогаются. Их деньги — общий котёл, и перебор конвертов
 * там уже виден как минус в «Не закреплено»: урезать здесь значило бы
 * вычесть одни и те же рубли второй раз.
 */
export function backedReserves(input: {
  accounts: readonly Account[];
  reserves: readonly Reserve[];
  obligations: readonly Obligation[];
  today: PlainDate;
}): Reserve[] {
  const byAccount = new Map<string, Reserve[]>();
  for (const r of input.reserves) {
    const list = byAccount.get(r.accountId) ?? [];
    list.push(r);
    byAccount.set(r.accountId, list);
  }

  /** Ближайший неоплаченный срок платежа: по нему конверт встаёт в очередь. */
  const nextDue = (id: string): string => {
    const o = input.obligations.find((x) => x.id === id);
    if (!o || !o.isActive) return '9999-12-31';
    const amount = instanceAmount(o);
    const dates = occurrencesBetween(
      obligationSchedule(o, input.today), addDays(input.today, -62), addDays(input.today, 400),
    );
    return dates.find((d) => paidFor(o, d, amount) < amount) ?? '9999-12-31';
  };
  const order = (r: Reserve): string => (r.forKind === 'obligation'
    ? `0:${nextDue(r.forId)}`
    : r.forKind === 'credit' ? '1:' : '2:');

  const out: Reserve[] = [];
  for (const [accountId, list] of byAccount) {
    const account = input.accounts.find((a) => a.id === accountId);
    const total = add(...list.map((r) => r.amount));
    if (!account || account.isLiquid || account.kind === 'credit_card' || total <= account.balance) {
      out.push(...list);
      continue;
    }
    let room = clampPos(account.balance);
    for (const r of [...list].sort((a, b) => order(a).localeCompare(order(b)))) {
      const take = minMoney(room, r.amount);
      room = sub(room, take);
      out.push({ ...r, amount: take });
    }
  }
  return out;
}

/**
 * Сколько требует наступление.
 *
 * Снимок суммы на момент решения сильнее текущей суммы: правка платежа не
 * переписывает прошлое. Оплату, записанную до начала нынешнего расписания и
 * без снимка, закрывает то, что по ней заплачено: сколько тогда требовалось,
 * неизвестно, а требовать доплату по новой сумме — выдумывать долг. Няня
 * стоила 6 000, 9-го оплатили 6 000, с 13-го она 9 000 — 9-е закрыто, а не
 * «не хватает 3 000».
 */
export function requiredFor(o: Obligation, fact: ObligationPayment | undefined): Money {
  if (fact?.requiredAt !== undefined) return fact.requiredAt;
  const from = o.schedule?.from;
  if (fact && fact.paid > ZERO && from && fact.scheduledDate < from) return fact.paid;
  return instanceAmount(o);
}

/** Состояние наступления — то, что видит человек, а не внутренняя сумма. */
export type InstanceState =
  | 'expected'
  | 'paid'
  | 'partial'
  | 'confirmed_unpaid'
  | 'not_applicable';

/** Запись решения по наступлению, если она есть. */
export const factFor = (
  o: Obligation, scheduledDate: PlainDate,
): ObligationPayment | undefined => o.payments?.find((p) => p.scheduledDate === scheduledDate);

/**
 * В каком состоянии наступление.
 *
 * Решение человека сильнее расчёта: подтверждённый долг остаётся долгом, а
 * «платежа не было» не воскресает от смены месяца или правки расписания.
 */
export function instanceState(o: Obligation, scheduledDate: PlainDate): InstanceState {
  const fact = factFor(o, scheduledDate);
  if (fact?.decision === 'not_applicable') return 'not_applicable';
  const required = requiredFor(o, fact);
  const paid = paidFor(o, scheduledDate, required);
  if (paid >= required && required > ZERO) return 'paid';
  if (paid > ZERO) return 'partial';
  return fact?.decision === 'unpaid' ? 'confirmed_unpaid' : 'expected';
}

/**
 * С какого дня приложение отвечает за наступления этого обязательства.
 *
 * Самая поздняя из трёх границ: начало наблюдения, начало действия
 * расписания и день, когда обязательство завели. Раньше неё автоматически
 * выведенные наступления просрочкой не объявляются — человек платил их мимо
 * приложения, и требовать за них отчёта значит выдумывать долг.
 */
export function watchFrom(
  o: Obligation, trackingStartedAt: PlainDate | undefined, today: PlainDate,
): PlainDate {
  // Дата заведения обязательства учитывалась бы здесь же, но её в модели
  // нет: требовать отчёта за наступления до того, как платёж вообще
  // появился, всё равно нельзя — это делает начало наблюдения.
  const bounds = [trackingStartedAt, o.schedule?.from]
    .filter((d): d is PlainDate => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d));
  /*
    Начала наблюдения нет вовсе — берём начало текущего месяца.

    Так бывает у данных, заведённых до появления этой даты, и в расчётах по
    чужому снимку. Это не правило, а запасной вариант: у живого человека
    дата есть всегда, `loadState` выводит её при первой же загрузке. Брать
    здесь «всю историю» нельзя — приложение придумало бы годы просрочки за
    платежи, которые человек исправно платил мимо него.
  */
  if (bounds.length === 0) return `${today.slice(0, 7)}-01` as PlainDate;
  return bounds.reduce((a, b) => (a >= b ? a : b));
}

export interface UnsettledInput {
  obligations: readonly Obligation[];
  reserves?: readonly Reserve[];
  today: PlainDate;
  /** С какого дня приложение ведёт учёт. */
  trackingStartedAt?: PlainDate;
  /** До какого дня смотреть вперёд. По умолчанию — только прошлое и сегодня. */
  to?: PlainDate;
}

/**
 * Единственный ответ на вопрос «какие платежи ещё ждут денег».
 *
 * До этой функции ответов было два: «Можно потратить» смотрело от начала
 * цикла, виджет — от начала месяца, раскладка — от сегодня. Они расходились
 * ровно там, где это дороже всего: неоплаченный платёж прошлого месяца
 * пропадал из защищённого слоя, и деньги, которые человек должен, выглядели
 * свободными.
 *
 * Правила, из-за которых функция не сводится к «взять все наступления»:
 *
 * · **граница наблюдения.** Автоматически выведенные наступления считаются
 *   не раньше, чем приложение начало следить (`watchFrom`).
 * · **решения сильнее расчёта.** Подтверждённый долг и частичная оплата
 *   учитываются независимо от возраста, «платежа не было» — не учитывается
 *   никогда.
 * · **одно неразрешённое на обязательство.** Недельный платёж, забытый на
 *   месяц, не имеет права превратиться в четыре карточки: сначала человек
 *   решает одну дату, потом появляется следующая.
 * · **календарь ничего не сбрасывает.** Ни первое число, ни новый год.
 */
export function unsettledInstances(input: UnsettledInput): PaymentInstance[] {
  const { today } = input;
  const to = input.to ?? today;
  const out: PaymentInstance[] = [];

  for (const o of input.obligations) {
    if (!o.isActive) continue;
    const since = watchFrom(o, input.trackingStartedAt, today);

    // Явные решения живут независимо от границы наблюдения и от окна: долг,
    // подтверждённый человеком, не перестаёт быть долгом от смены месяца.
    const decided = (o.payments ?? [])
      .filter((p) => p.decision === 'unpaid' || (p.paid > ZERO && p.decision !== 'not_applicable'))
      .map((p) => p.scheduledDate);

    // Автоматически выведенные наступления — от начала наблюдения до `to`.
    const derived = occurrencesBetween(obligationSchedule(o, today), since, to);

    const dates = [...new Set([...decided, ...derived])].sort();
    const instances = instancesFor(o, dates, input.reserves ?? [], today);

    // Неразрешённое показываем по одному: самое раннее после начала
    // наблюдения. Решит — появится следующее.
    let unresolvedShown = false;
    for (const inst of instances) {
      const state = instanceState(o, inst.scheduledDate);
      if (state === 'paid' || state === 'not_applicable') continue;
      if (state === 'expected') {
        // Будущее наступление в окне — не «неразрешённое прошлое»: оно ждёт
        // своего срока и показывается как обычно.
        const future = isAfter(inst.scheduledDate, today);
        if (!future) {
          if (unresolvedShown) continue;
          unresolvedShown = true;
        }
      }
      out.push(inst);
    }
  }

  return out.sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
}

/** Наступления по готовому списку дат — с конвертами и снимками сумм. */
function instancesFor(
  o: Obligation, dates: readonly PlainDate[], reserves: readonly Reserve[], today: PlainDate,
): PaymentInstance[] {
  let envelope = add(
    ...reserves.filter((r) => r.forKind === 'obligation' && r.forId === o.id).map((r) => r.amount),
  );
  const out: PaymentInstance[] = [];
  for (const scheduledDate of dates) {
    const fact = factFor(o, scheduledDate);
    // Сумма требования — снимок на момент решения, если он есть: правка
    // платежа не переписывает прошлое.
    const amount = requiredFor(o, fact);
    const paid = paidFor(o, scheduledDate, amount);
    const unpaid = clampPos(sub(amount, paid));
    const settled = unpaid === ZERO;
    const secured = minMoney(envelope, unpaid);
    envelope = sub(envelope, secured);
    out.push({
      id: `${o.id}:${scheduledDate}`,
      obligationId: o.id,
      label: o.name,
      scheduledDate,
      amount,
      paid,
      unpaid,
      secured,
      remaining: clampPos(sub(unpaid, secured)),
      settled,
      due: !settled && !isAfter(scheduledDate, today),
      accountId: o.accountId,
    });
  }
  return out;
}

/**
 * Сколько оплачено по наступлению.
 *
 * Записи, сделанные до появления фактов оплаты, знают только последний
 * подтверждённый цикл. Для них подтверждение означает полную оплату — иначе
 * платёж, закрытый в прошлом месяце, внезапно снова потребовал бы денег.
 */
function paidFor(o: Obligation, scheduledDate: PlainDate, amount: Money): Money {
  const own = ownPaid(o, scheduledDate, amount);
  // Наступление уже закрыто само — переносить в него нечего.
  if (own >= amount) return own;
  return minMoney(add(own, carriedInto(o, scheduledDate)), amount);
}

/**
 * Переплата прошлых наступлений — в счёт следующих (16.10.2026).
 *
 * Няне за неделю 9 000 ₽, отдали 10 000: не было сдачи. Лишняя 1 000 ₽ — не
 * подарок, а часть оплаты следующей недели, и там остаётся 8 000. Раньше она
 * пропадала: наступление закрыто, а следующее требовало всю сумму.
 *
 * Только у постоянной суммы. У меняющейся (ЖКХ) оплата больше оценки — это
 * такой пришёл счёт, лишнего в ней нет. И не у кредита: сверх платежа по
 * графику — досрочное погашение, оно уменьшает долг, а не следующий платёж
 * (21.10.2026). Его записывает лист оплаты отдельной операцией.
 */
function carriedInto(o: Obligation, scheduledDate: PlainDate): Money {
  if (o.amountType !== 'fixed' || o.kind === 'loan' || o.kind === 'credit_card_payment') return ZERO;
  const before = (o.payments ?? []).filter((p) => p.scheduledDate < scheduledDate);
  if (!before.some((p) => p.decision !== 'not_applicable' && p.paid > requiredFor(o, p))) return ZERO;
  const first = before.reduce((a, p) => (p.scheduledDate < a ? p.scheduledDate : a), scheduledDate);
  // Даты по расписанию и даты оплат: расписание могли поменять, а оплата осталась.
  const dates = [...new Set([
    ...occurrencesBetween(obligationSchedule(o, first), first, addDays(scheduledDate, -1)),
    ...before.map((p) => p.scheduledDate),
  ])].sort();
  let carry = ZERO;
  for (const d of dates) {
    const fact = factFor(o, d);
    // «Платежа не было» денег не просит — переплата идёт дальше целиком.
    if (fact?.decision === 'not_applicable') continue;
    const required = requiredFor(o, fact);
    carry = clampPos(sub(add(ownPaid(o, d, required), carry), required));
  }
  return carry;
}

/** Оплачено по самому наступлению — без переноса из прошлых. */
function ownPaid(o: Obligation, scheduledDate: PlainDate, amount: Money): Money {
  const fact = o.payments?.find((p) => p.scheduledDate === scheduledDate);
  // «Платежа не было» — не оплата, а отмена требования: денег он не просит
  // и оплаченным не считается.
  if (fact?.decision === 'not_applicable') return amount;
  /*
    Сумма меняется (ЖКХ, связь) — оплата закрывает наступление целиком:
    счёт пришёл на 8 592,72 ₽, а оценка была 10 000 ₽, и «осталось 1 407 ₽»
    было бы неправдой — больше по этому счёту платить нечего.
  */
  if (fact && o.amountType === 'variable' && fact.paid > ZERO) return fact.paid > amount ? fact.paid : amount;
  if (fact) return fact.paid;
  return o.settledCycle === scheduledDate ? amount : ZERO;
}

/**
 * Обязательная нагрузка за период — по фактическому календарю.
 *
 * Это замена суммированию «всех активных обязательств»: годовая страховка
 * входит в свой месяц целиком и не входит в остальные одиннадцать.
 */
export function obligationsInPeriod(
  obligations: readonly Obligation[],
  today: PlainDate,
  from: PlainDate,
  to: PlainDate,
): Money {
  return add(...paymentInstances({ obligations, today, from, to }).map((i) => i.amount));
}

/**
 * Деньги, которые уже обещаны обязательным платежам.
 *
 * Считается неоплаченное: платёж, который человек уже провёл, деньги повседневные нужды
 * больше не связывает. Нужно карточке «Повседневные нужды» — её крупное число обязано
 * называть то, что можно потратить, а не остаток строки бюджета, внутри
 * которого сидят ипотека и рассрочка ближайших дней.
 *
 * Считается **единым селектором** `unsettledInstances`, а не своим окном.
 * Сначала здесь брались наступления впереди, потом — от начала месяца, и оба
 * раза просроченный платёж выпадал: сперва вчерашний, потом прошлогодний.
 * Календарь вообще ничего не решает — решает начало наблюдения и решения
 * человека.
 *
 * Отсюда инвариант, ради которого это и написано: **запись платежа не меняет
 * «ещё можно»**. Деньги переходят из «обещано» в «потрачено», а разность
 * остаётся прежней — ровно как оплата обеспеченного платежа не меняет «Можно
 * потратить» (см. снятие конверта в `store.ts`).
 */
export function obligationsOwed(
  obligations: readonly Obligation[],
  today: PlainDate,
  trackingStartedAt?: PlainDate,
): Money {
  const { year, month } = parts(today);
  return add(...unsettledInstances({
    obligations,
    today,
    ...(trackingStartedAt ? { trackingStartedAt } : {}),
    // До конца месяца вперёд: карточка «Повседневные нужды» показывает месяц целиком.
    // Прошлое приходит из самого селектора и календарём не ограничено.
    to: ymd(year, month, daysInMonth(year, month)),
  }).map((i) => i.unpaid));
}

/**
 * Обязательная нагрузка календарного месяца.
 *
 * Период плана — календарный месяц (решение §26.1 плана миграции): платежи
 * привязаны к числам, аналитика уже месячная, а у нерегулярного дохода цикла
 * нет вовсе. Годовой платёж входит в свой месяц целиком и не размазывается.
 */
export function monthlyObligations(
  obligations: readonly Obligation[],
  today: PlainDate,
): Money {
  const { year, month } = parts(today);
  return obligationsInPeriod(
    obligations, today,
    ymd(year, month, 1),
    ymd(year, month, daysInMonth(year, month)),
  );
}
